import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { format, isValid } from 'date-fns';

import { useToggle } from '@hooks';

const GoalReportContext = createContext<GoalContextValue | null>(null);

export const useGoalReportContext = () => {
    const context = useContext(GoalReportContext);
    if (!context) {
        throw new Error('useGoalReportContext must be used within a GoalReportContext');
    }
    return context;
};

export const lastSwingsOption = [
    { label: 'Last 6', value: 'last6' },
    { label: 'Last 10', value: 'last10' },
    { label: 'Last 20', value: 'last20' },
    { label: 'All', value: 'all' },
] as const;

export const periodOptions = [
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
    { label: 'All', value: 'all' },
    { label: 'Custom', value: 'custom' },
] as const;

export type PeriodFilter = (typeof periodOptions)[number]['value'];
export type SwingsFilter = (typeof lastSwingsOption)[number]['value'];

export type GoalFilters = {
    period?: PeriodFilter;
    swings?: SwingsFilter;
};
/**
 * Strings are stored in YYYY-MM-DD format for the native date elements
 */
export type CustomRangeInput = {
    from?: string;
    to?: string;
};

type GoalContextValue = {
    layoutId: number;
    activeUserId?: string;
    filters: Partial<GoalFilters>;
    uiFilters: GoalFilters;
    updateFilters: (val: Partial<GoalFilters>) => void;
    customRange: CustomRangeInput;
    setCustomRange: (range: CustomRangeInput) => void;
    resetFilters: () => void;
    showFilters: boolean;
    toggleShowFilters: () => void;
    minDate: string;
    maxDate: string;
    getFullActivityRoute: (activity: number, uuid: string) => string;
};

type GoalReportProviderProps = {
    layoutId: number;
    activeUserId?: string;
    reportPeriod: { from: Date; to: Date } | null;
    getFullActivityRoute: (activity: number, uuid: string) => string;
};

export const GoalReportProvider = ({
    children,
    layoutId,
    activeUserId,
    reportPeriod,
    getFullActivityRoute,
}: PropsWithChildren<GoalReportProviderProps>) => {
    // We maintain two filter states:
    // - `filters` this is what we actually filter by
    // - `uiFilters` this is the ui state of the toggles - display only
    const [filters, setFilters] = useState<GoalFilters>({ period: 'month', swings: undefined });
    const [uiFilters, setUiFilters] = useState<GoalFilters>({ period: 'month', swings: 'all' });

    const [showFilters, toggleShowFilters] = useToggle(false);
    const minDate = useMemo(
        () => (reportPeriod?.from && isValid(reportPeriod?.from) ? format(reportPeriod?.from, 'yyyy-MM-dd') : ''),
        [reportPeriod?.from],
    );
    const maxDate = useMemo(
        () => (reportPeriod?.to && isValid(reportPeriod?.to) ? format(reportPeriod?.to, 'yyyy-MM-dd') : ''),
        [reportPeriod?.to],
    );
    const [customRange, setCustomRange] = useState<CustomRangeInput>({
        from: minDate,
        to: maxDate,
    });

    const updateCustomRange = useCallback((range: Partial<CustomRangeInput>) => {
        setCustomRange((prev) => ({
            ...prev,
            ...range,
        }));
    }, []);

    const updateFilters = useCallback((filter: Partial<GoalFilters>) => {
        setUiFilters((prev) => ({ ...prev, ...filter }));
        setFilters(filter);
    }, []);

    const resetFilters = useCallback(() => {
        setFilters({ period: 'all', swings: undefined });
        setUiFilters({ period: 'all', swings: 'all' });
        setCustomRange({ from: minDate, to: maxDate });
    }, []);

    const value: GoalContextValue = {
        layoutId,
        activeUserId,
        uiFilters,
        filters,
        updateFilters,
        customRange,
        setCustomRange: updateCustomRange,
        showFilters,
        minDate,
        maxDate,
        resetFilters,
        toggleShowFilters,
        getFullActivityRoute,
    };
    return <GoalReportContext.Provider value={value}>{children}</GoalReportContext.Provider>;
};
